import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import logoSrc from "../sauntera-wordmark.png";

export function Layout({ children }: { children: React.ReactNode }) {
  const { signOut, user } = useAuth();
  // Mobile: sidebar is an off-canvas drawer toggled from the top bar.
  // Desktop (>800px) ignores this state entirely — the sidebar is static.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Navigating closes the drawer — NavLink taps land on a new route, and
  // leaving the drawer open over the new screen reads as a bug.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the drawer is open so the page underneath
  // doesn't scroll on touch devices.
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  return (
    <div className="layout">
      {/* Mobile-only top bar: hamburger + wordmark. Hidden on desktop. */}
      <header className="mobile-topbar">
        <button
          type="button"
          className="hamburger"
          aria-label={drawerOpen ? "Close menu" : "Open menu"}
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen((v) => !v)}
        >
          {drawerOpen ? "✕" : "☰"}
        </button>
        <img src={logoSrc} alt="Sauntera" className="mobile-topbar-logo" />
      </header>

      {/* Backdrop behind the drawer — tap to dismiss. */}
      {drawerOpen && (
        <div
          className="drawer-backdrop"
          aria-hidden="true"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <aside className={`sidebar${drawerOpen ? " open" : ""}`}>
        {/* Brand header — wordmark above a "PINTEREST" caption that's
            stretched to span the exact same width as the logo. SVG +
            textLength/lengthAdjust gives a deterministic spread; CSS
            letter-spacing alone can't match an arbitrary image width. */}
        <div className="brand">
          <img src={logoSrc} alt="Sauntera" className="brand-logo" />
          <svg
            className="brand-sub"
            viewBox="0 0 100 12"
            preserveAspectRatio="xMidYMid meet"
            aria-label="Pinterest"
          >
            <text
              x="0"
              y="10"
              textLength="100"
              lengthAdjust="spacing"
              fontSize="10"
              fontWeight="700"
              fill="currentColor"
            >
              PINTEREST
            </text>
          </svg>
        </div>

        {/* User identity + sign-out moved up from the previous flex-spacer
            footer. With the nav list short, the old layout was already
            above-the-fold on tall screens, but on shorter laptop displays
            the sign-out got pushed below; placing it here guarantees it's
            visible without scrolling. */}
        {user && (
          <div className="user-block">
            <div className="user-email" title={user.email ?? ""}>{user.email}</div>
            <button type="button" className="signout" onClick={() => signOut()}>
              Sign out
            </button>
          </div>
        )}

        <div className="nav-divider" aria-hidden="true" />

        <div className="nav-section">Sauntera Date Idea Pins</div>
        <NavLink to="/app" end>All pins</NavLink>
        <NavLink to="/pins/new">Date Ideas pin</NavLink>
        <NavLink to="/fifa">🏆 FIFA 2026</NavLink>

        <div className="nav-section">Sauntera Wedding Pins</div>
        <NavLink to="/wedding-pins">All pins</NavLink>
        <NavLink to="/wedding-studio">💍 Wedding pin</NavLink>
        <NavLink to="/daily">📅 Daily pins</NavLink>
        <NavLink to="/articles">📝 Blog</NavLink>

        <div className="nav-divider" aria-hidden="true" />
        <NavLink to="/clients">💼 Clients</NavLink>
        <NavLink to="/client-pins">📋 All client pins</NavLink>
        <NavLink to="/boards">📌 Boards</NavLink>
        <NavLink to="/playbook">📖 Playbook</NavLink>
        <NavLink to="/settings">Settings</NavLink>
      </aside>
      <main className="main">{children}</main>
    </div>
  );
}
